import type { CSSProperties } from "react";

/** Los cinco estados que el operador ve, más los dos que viven fuera de Magento. */
export const ESTADOS: Record<
  string,
  { label: string; color: string; style?: CSSProperties }
> = {
  draft: { label: "Borrador", color: "default" },
  review: { label: "En revisión", color: "gold" },
  active: { label: "Activo", color: "green" },
  inactive: { label: "Pausado", color: "yellow" },
  // Archivado no es un color de antd: borde negro y fondo claro, los mismos del encabezado
  // de su sección en la ficha (`.estado-seccion--archived`). Antes era rojo.
  archived: {
    label: "Archivado",
    color: "default",
    style: {
      color: "rgba(0, 0, 0, 0.88)",
      background: "#fafafa",
      borderColor: "#000000",
    },
  },
};

/**
 * Los tres estados de un producto publicado, que el índice expone como `state`
 * (xcons-docs#380). Antes el filtro usaba `status`, que es derivado —`active → 1`, todo lo
 * demás `→ 2`—: Pausado y Archivado eran el mismo `2`, así que no se podía ofrecer
 * Archivado y "Pausado" traía los archivados mezclados.
 */
export const ESTADOS_PUBLICADOS = ["active", "inactive", "archived"] as const;
export type EstadoPublicado = (typeof ESTADOS_PUBLICADOS)[number];

export const esEstadoPublicado = (valor?: string): valor is EstadoPublicado =>
  (ESTADOS_PUBLICADOS as readonly string[]).includes(valor ?? "");

/**
 * El estado de un producto del índice. Sale de `state`; un record todavía sin reindexar
 * no lo trae, y ahí se cae al `status` derivado, que no distingue Archivado de Pausado.
 */
export const estadoDelHit = (hit: {
  state?: string | false;
  status?: number | false;
}): EstadoPublicado =>
  esEstadoPublicado(hit.state || undefined)
    ? (hit.state as EstadoPublicado)
    : hit.status === 1
      ? "active"
      : "inactive";

/**
 * La etiqueta de un valor del filtro de Estado. Los valores ahora **son** claves de
 * `ESTADOS`, pero se conserva el fallback: una clave que no está no puede devolver
 * `undefined`, porque pedirle `.label` tumbaba el render entero, sin error boundary.
 */
export const etiquetaEstado = (valor: string): string =>
  ESTADOS[valor]?.label ?? valor;
